import type { ThemeAssetEntry } from '@goodtime/engine';
import { BIOME_BY_THEME_ID, ROAD_THEMES, worldOf } from '@goodtime/shared';
import roadRingForestUrl from '../assets/road/ring_forest.png';
import roadRingRoyalUrl from '../assets/road/ring_royal.png';
import roadRingUnderwaterUrl from '../assets/road/ring_underwater.png';
import roadRingFrostUrl from '../assets/road/ring_frost.png';
import roadRingFrontierUrl from '../assets/road/ring_frontier.png';
import roadRingNeonUrl from '../assets/road/ring_neon.png';
import roadRingCaravanUrl from '../assets/road/ring_caravan.png';
import roadRingSherbetSquareUrl from '../assets/road/ring_sherbet_square.png';
import roadHubRoyalUrl from '../assets/road/hub_royal.png';
import roadHubUnderwaterUrl from '../assets/road/hub_underwater.png';
import roadHubFrostUrl from '../assets/road/hub_frost.png';
import roadHubFrontierUrl from '../assets/road/hub_frontier.png';
import roadHubNeonUrl from '../assets/road/hub_neon.png';
import roadHubCaravanUrl from '../assets/road/hub_caravan.png';
import roadHubSherbetSquareUrl from '../assets/road/hub_sherbet_square.png';
import forestAcornUrl from '../assets/reel/forest/acorn.png';
import royalLifeUrl from '../assets/road/life_royal.png';
import underwaterLifeUrl from '../assets/road/life_underwater.png';
import frostLifeUrl from '../assets/road/life_frost.png';
import frontierLifeUrl from '../assets/road/life_frontier.png';
import neonLifeUrl from '../assets/road/life_neon.png';
import caravanLifeUrl from '../assets/road/life_caravan.png';
import sherbetSquareLifeUrl from '../assets/road/life_sherbet_square.png';
import royalReelsNodeUrl from '../assets/map_node/royal_reels.png';
import gardensReelsNodeUrl from '../assets/map_node/gardens_reels.png';
import mapFragmentReelsNodeUrl from '../assets/map_node/map_fragment_reels.png';
import avalancheReelsNodeUrl from '../assets/map_node/avalanche_reels.png';
import goldMineReelsNodeUrl from '../assets/map_node/gold_mine_reels.png';
import arcadeReelsNodeUrl from '../assets/map_node/arcade_reels.png';
import pharaohTombReelsNodeUrl from '../assets/map_node/pharaoh_tomb_reels.png';
import lollipopLaneReelsNodeUrl from '../assets/map_node/lollipop_lane_reels.png';
import royalPathUrl from '../assets/road/path_strip_royal.png';
import underwaterPathUrl from '../assets/road/path_strip_underwater.png';
import frostPathUrl from '../assets/road/path_strip_frost.png';
import frontierPathUrl from '../assets/road/path_strip_frontier.png';
import neonPathUrl from '../assets/road/path_strip_neon.png';
import caravanPathUrl from '../assets/road/path_strip_caravan.png';
import sherbetSquarePathUrl from '../assets/road/path_strip_sherbet_square.png';

/*
 * Each Royal Road slot's own art: its Lamp Run ring, the plate under the run
 * pot, its life token, its landmarks and the map painting of the slot its
 * chapter opens. Every alias names the slot, because an alias keeps the
 * first picture it was given (AssetLoader): two roads sharing one would
 * both show whichever loaded first.
 */

/**
 * The Lamp Run ring for each Royal Road slot, as `road:ring_<theme id>`.
 * BoardRing draws a plain ring for a theme without one. A slot needs one to
 * get any road art at all (themes/index.ts).
 */
export const ROAD_RINGS: Record<string, ThemeAssetEntry> = {
  forest: { alias: 'road:ring_forest', src: roadRingForestUrl, path: 'road/ring_forest.png' },
  royal: { alias: 'road:ring_royal', src: roadRingRoyalUrl, path: 'road/ring_royal.png' },
  underwater: { alias: 'road:ring_underwater', src: roadRingUnderwaterUrl, path: 'road/ring_underwater.png' },
  frost: { alias: 'road:ring_frost', src: roadRingFrostUrl, path: 'road/ring_frost.png' },
  frontier: { alias: 'road:ring_frontier', src: roadRingFrontierUrl, path: 'road/ring_frontier.png' },
  neon: { alias: 'road:ring_neon', src: roadRingNeonUrl, path: 'road/ring_neon.png' },
  caravan: { alias: 'road:ring_caravan', src: roadRingCaravanUrl, path: 'road/ring_caravan.png' },
  sherbet_square: {
    alias: 'road:ring_sherbet_square',
    src: roadRingSherbetSquareUrl,
    path: 'road/ring_sherbet_square.png',
  },
};

/** The plate under the run pot in the ring's hub, as `road:hub_<theme id>`.
 *  A slot without one (Forest) shows the pot on the ring's own middle. */
export const ROAD_HUBS: Record<string, ThemeAssetEntry> = {
  royal: { alias: 'road:hub_royal', src: roadHubRoyalUrl, path: 'road/hub_royal.png' },
  underwater: { alias: 'road:hub_underwater', src: roadHubUnderwaterUrl, path: 'road/hub_underwater.png' },
  frost: { alias: 'road:hub_frost', src: roadHubFrostUrl, path: 'road/hub_frost.png' },
  frontier: { alias: 'road:hub_frontier', src: roadHubFrontierUrl, path: 'road/hub_frontier.png' },
  neon: { alias: 'road:hub_neon', src: roadHubNeonUrl, path: 'road/hub_neon.png' },
  caravan: { alias: 'road:hub_caravan', src: roadHubCaravanUrl, path: 'road/hub_caravan.png' },
  sherbet_square: {
    alias: 'road:hub_sherbet_square',
    src: roadHubSherbetSquareUrl,
    path: 'road/hub_sherbet_square.png',
  },
};

/** Each road slot's life token, shown as the run's lives
 *  (`road:life_<theme id>`): acorns on Forest, shields on Royal Reels,
 *  lifebuoys, bobble hats, horseshoes, light bulbs, ankhs and wrapped sweets
 *  on the six worlds' first slots. */
export const ROAD_LIFE: Record<string, ThemeAssetEntry> = {
  forest: { alias: 'road:life_forest', src: forestAcornUrl, path: 'reel/forest/acorn.png' },
  royal: { alias: 'road:life_royal', src: royalLifeUrl, path: 'road/life_royal.png' },
  underwater: { alias: 'road:life_underwater', src: underwaterLifeUrl, path: 'road/life_underwater.png' },
  frost: { alias: 'road:life_frost', src: frostLifeUrl, path: 'road/life_frost.png' },
  frontier: { alias: 'road:life_frontier', src: frontierLifeUrl, path: 'road/life_frontier.png' },
  neon: { alias: 'road:life_neon', src: neonLifeUrl, path: 'road/life_neon.png' },
  caravan: { alias: 'road:life_caravan', src: caravanLifeUrl, path: 'road/life_caravan.png' },
  sherbet_square: {
    alias: 'road:life_sherbet_square',
    src: sherbetSquareLifeUrl,
    path: 'road/life_sherbet_square.png',
  },
};

/** Each world's road surface, as `road:path_strip_<theme id>`: the strip
 *  the Lamp Run panel's road and the Sweet Trail roll along, tiled. Forest
 *  has none of its own: its cobbles are the shared `road:path_strip`, which
 *  also stands in for any world without one. */
export const ROAD_PATHS: Record<string, ThemeAssetEntry> = {
  royal: { alias: 'road:path_strip_royal', src: royalPathUrl, path: 'road/path_strip_royal.png' },
  underwater: { alias: 'road:path_strip_underwater', src: underwaterPathUrl, path: 'road/path_strip_underwater.png' },
  frost: { alias: 'road:path_strip_frost', src: frostPathUrl, path: 'road/path_strip_frost.png' },
  frontier: { alias: 'road:path_strip_frontier', src: frontierPathUrl, path: 'road/path_strip_frontier.png' },
  neon: { alias: 'road:path_strip_neon', src: neonPathUrl, path: 'road/path_strip_neon.png' },
  caravan: { alias: 'road:path_strip_caravan', src: caravanPathUrl, path: 'road/path_strip_caravan.png' },
  sherbet_square: {
    alias: 'road:path_strip_sherbet_square',
    src: sherbetSquarePathUrl,
    path: 'road/path_strip_sherbet_square.png',
  },
};

/** The road world whose path a biome's slots roll along on the Sweet
 *  Trail. The kingdom's slots keep Forest's cobbles. */
export const PATH_WORLD_OF_BIOME: Record<string, string> = {
  cove: 'underwater',
  frost: 'frost',
  frontier: 'frontier',
  neon: 'neon',
  egypt: 'caravan',
  candy: 'sherbet_square',
};

/** A slot's road pieces: its ring, the plate under its run pot, its life
 *  token and its road surface. */
export interface RoadPieces {
  ring: ThemeAssetEntry;
  hub?: ThemeAssetEntry;
  life?: ThemeAssetEntry;
  path?: ThemeAssetEntry;
}

/**
 * The road pieces a slot draws with, or null for a slot that can have no
 * road. A road slot has its own. A Wheel Run slot uses its world road's,
 * under that road's aliases (the same pictures, so they're fetched once):
 * the kingdom's slots take Royal Reels' ring, hub and shields, and roll on
 * Forest's cobbles, as their Sweet Trail did.
 */
export function roadPiecesFor(themeId: string): RoadPieces | null {
  const world = worldOf(themeId);
  const ring = ROAD_RINGS[world];
  if (!ring) return null;
  const biome = BIOME_BY_THEME_ID[themeId];
  const pathWorld = ROAD_THEMES.includes(themeId) ? themeId : biome ? PATH_WORLD_OF_BIOME[biome] : undefined;
  return {
    ring,
    hub: ROAD_HUBS[world],
    life: ROAD_LIFE[world],
    path: pathWorld ? ROAD_PATHS[pathWorld] : undefined,
  };
}

/** The map painting of the slot each road's chapter opens (shared
 *  ROAD_NEXT_SLOT), as `road:next_slot_<theme id>`, for the chapter card. */
export const ROAD_NEXT_SLOT_ART: Record<string, ThemeAssetEntry> = {
  forest: { alias: 'road:next_slot_forest', src: royalReelsNodeUrl, path: 'map_node/royal_reels.png' },
  royal: { alias: 'road:next_slot_royal', src: gardensReelsNodeUrl, path: 'map_node/gardens_reels.png' },
  underwater: {
    alias: 'road:next_slot_underwater',
    src: mapFragmentReelsNodeUrl,
    path: 'map_node/map_fragment_reels.png',
  },
  frost: { alias: 'road:next_slot_frost', src: avalancheReelsNodeUrl, path: 'map_node/avalanche_reels.png' },
  frontier: { alias: 'road:next_slot_frontier', src: goldMineReelsNodeUrl, path: 'map_node/gold_mine_reels.png' },
  neon: { alias: 'road:next_slot_neon', src: arcadeReelsNodeUrl, path: 'map_node/arcade_reels.png' },
  caravan: {
    alias: 'road:next_slot_caravan',
    src: pharaohTombReelsNodeUrl,
    path: 'map_node/pharaoh_tomb_reels.png',
  },
  sherbet_square: {
    alias: 'road:next_slot_sherbet_square',
    src: lollipopLaneReelsNodeUrl,
    path: 'map_node/lollipop_lane_reels.png',
  },
};

/**
 * Each road slot's landmarks, ruined and restored (assets/road/landmark/
 * <theme>/<id>_<state>.png), as `road:landmark:<id>:<state>`, and the map
 * painting of the slot its chapter opens (Phase 2a). They stand in a row on
 * their ground; each is sized by its painted area, measured from its alpha
 * when the road is drawn.
 */
export const ROAD_LANDMARKS: Record<string, Record<string, ThemeAssetEntry>> = {};
for (const [file, mod] of Object.entries(
  import.meta.glob<{ default: string }>('../assets/road/landmark/*/*.png', { eager: true }),
)) {
  const [, theme, id, state] = /landmark\/([^/]+)\/(.+)_(restored|ruined)\.png$/.exec(file) ?? [];
  if (!theme || !id || !state) continue;
  (ROAD_LANDMARKS[theme] ??= {
    ...(ROAD_NEXT_SLOT_ART[theme] ? { next_slot: ROAD_NEXT_SLOT_ART[theme] } : {}),
  })[`landmark_${id}_${state}`] = {
    alias: `road:landmark:${id}:${state}`,
    src: mod.default,
    path: `road/landmark/${theme}/${id}_${state}.png`,
  };
}
